import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Icons } from '../common';
import qcService from '../../services/qcService';

const QCAnalysisReport = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [reanalyzing, setReanalyzing] = useState(false);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await axios.get(`/api/qc/analysis/${analysisId}`);
        setAnalysis(response.data);
        // Expand all categories by default
        const categories = {};
        response.data.issues?.forEach(issue => {
          categories[issue.category] = true;
        });
        setExpandedCategories(categories);
      } catch (error) {
        console.error('Failed to fetch analysis:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [analysisId]);

  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      const response = await qcService.reanalyze(analysisId);
      setAnalysis(response.data);
      const categories = {};
      response.data.issues?.forEach(issue => {
        categories[issue.category] = true;
      });
      setExpandedCategories(categories);
    } catch (error) {
      console.error('Reanalyze failed:', error);
    } finally {
      setReanalyzing(false);
    }
  };

  const handleExport = () => {
    if (!analysis) return;
    const headers = ['Category', 'Severity', 'Location', 'Description', 'Suggested Fix'];
    const rows = (analysis.issues || []).map(issue => [
      issue.category || '',
      issue.severity || '',
      issue.location || '',
      issue.description || '',
      issue.suggestedFix || '',
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qc-report-${analysis.uploadedFilename || analysisId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading analysis...</p>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.ExclamationIcon className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-gray-900 font-semibold">Analysis not found</p>
        </div>
      </div>
    );
  }

  const issuesByCategory = analysis.issues?.reduce((acc, issue) => {
    if (!acc[issue.category]) acc[issue.category] = [];
    acc[issue.category].push(issue);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PASSED':
        return { bg: 'from-green-500 to-emerald-500', text: 'text-green-700', badge: 'bg-green-100 text-green-700' };
      case 'FAILED':
        return { bg: 'from-red-500 to-rose-500', text: 'text-red-700', badge: 'bg-red-100 text-red-700' };
      default:
        return { bg: 'from-yellow-500 to-amber-500', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' };
    }
  };

  const categoryIcons = {
    abbreviations: Icons.SparklesIcon,
    gene_formatting: Icons.BeakerIcon,
    language: Icons.LanguageIcon,
    author_information: Icons.UserGroupIcon,
    references: Icons.BookOpenIcon,
    math_operators: Icons.CalculatorIcon,
    article_structure: Icons.DocumentIcon,
    journal_rules: Icons.CheckCircleIcon,
  };

  const colors = getStatusColor(analysis.overallStatus);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate('/qc/dashboard')}
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium mb-4 transition-colors"
          >
            <Icons.ChevronLeftIcon className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">QC Analysis Report</h1>
              <p className="text-gray-600 mt-2">{analysis.uploadedFilename}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReanalyze}
                disabled={reanalyzing}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-900 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reanalyzing ? 'Re-analyzing…' : 'Re-analyze'}
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium hover:shadow-lg transition-all"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Overall Status */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50 p-6 overflow-hidden relative">
            <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} opacity-5`} />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-600">Overall Status</span>
                <Icons.CheckCircleIcon className={`w-5 h-5 ${colors.text}`} />
              </div>
              <p className={`text-3xl font-bold ${colors.text}`}>{analysis.overallStatus}</p>
            </div>
          </div>

          {/* Overall Score */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-600">Overall Score</span>
              <div className="relative w-12 h-12">
                <svg className="w-12 h-12 -rotate-90" viewBox="0 0 60 60">
                  <circle cx="30" cy="30" r="27" fill="none" stroke="#e5e7eb" strokeWidth="4" />
                  <circle
                    cx="30"
                    cy="30"
                    r="27"
                    fill="none"
                    stroke="url(#scoreGrad)"
                    strokeWidth="4"
                    strokeDasharray={`${analysis.overallScore * 1.7} 170`}
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#4f46e5" />
                      <stop offset="100%" stopColor="#2563eb" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-gray-900">
                  {analysis.overallScore}
                </span>
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{analysis.overallScore}%</p>
          </div>

          {/* Critical Issues */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50 p-6 overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-rose-500 opacity-5" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-600">Critical Issues</span>
                <Icons.ExclamationIcon className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-3xl font-bold text-red-600">{analysis.criticalIssuesCount}</p>
            </div>
          </div>

          {/* Warnings */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50 p-6 overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 to-amber-500 opacity-5" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-600">Warnings</span>
                <Icons.AlertIcon className="w-5 h-5 text-yellow-600" />
              </div>
              <p className="text-3xl font-bold text-yellow-600">{analysis.warningsCount}</p>
            </div>
          </div>
        </div>

        {/* Issues by Category */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200/50 overflow-hidden">
          <div className="p-8 border-b border-gray-200/50">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-indigo-600 to-blue-600 rounded-full" />
              Detailed Findings
            </h2>
          </div>

          <div className="divide-y divide-gray-200/50">
            {Object.entries(issuesByCategory || {}).map(([category, issues]) => {
              const Icon = categoryIcons[category] || Icons.DocumentIcon;
              const isExpanded = expandedCategories[category];

              return (
                <div key={category} className="border-b border-gray-200/50 last:border-b-0">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50/50 transition-colors group"
                  >
                    <div className="flex items-center gap-4 text-left">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-blue-100 flex items-center justify-center group-hover:shadow-lg transition-shadow">
                        <Icon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 capitalize">
                          {category.replace(/_/g, ' ')}
                        </h3>
                        <p className="text-sm text-gray-600">{issues.length} issue{issues.length !== 1 ? 's' : ''} found</p>
                      </div>
                    </div>
                    <div className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                      <Icons.ChevronDownIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                    </div>
                  </button>

                  {/* Category Issues */}
                  {isExpanded && (
                    <div className="px-8 py-4 bg-gray-50/30 space-y-4 animate-in fade-in slide-in-from-top-4">
                      {issues.map((issue, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-xl border-l-4 ${
                            issue.severity === 'CRITICAL'
                              ? 'bg-red-50 border-l-red-500'
                              : 'bg-yellow-50 border-l-yellow-500'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-lg ${
                              issue.severity === 'CRITICAL'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              <span className={`w-2 h-2 rounded-full ${
                                issue.severity === 'CRITICAL' ? 'bg-red-600' : 'bg-yellow-600'
                              }`} />
                              {issue.severity}
                            </span>
                            <span className="text-xs text-gray-600 bg-white px-3 py-1 rounded-lg">
                              {issue.location}
                            </span>
                          </div>

                          <p className="text-gray-900 font-medium mb-3">{issue.description}</p>

                          {issue.suggestedFix && (
                            <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200">
                              <p className="text-xs font-semibold text-gray-600 mb-1">Suggested Fix</p>
                              <p className="text-sm text-gray-800 font-mono">{issue.suggestedFix}</p>
                            </div>
                          )}

                          {issue.authorQuery && (
                            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                              <p className="text-xs font-semibold text-indigo-600 mb-1">Author Query</p>
                              <p className="text-sm text-indigo-900 italic">"{issue.authorQuery}"</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* No Issues */}
          {(!issuesByCategory || Object.keys(issuesByCategory).length === 0) && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Icons.CheckCircleIcon className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-lg font-semibold text-gray-900 mb-2">Perfect! No issues found.</p>
              <p className="text-gray-600">This document passed all QC checks.</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-12 flex gap-4 justify-center">
          <button
            onClick={() => navigate('/qc/articles')}
            className="px-8 py-4 rounded-2xl border-2 border-gray-300 text-gray-900 font-semibold hover:bg-gray-50 transition-colors duration-300"
          >
            View Other Articles
          </button>
          <button
            onClick={handleExport}
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            Export Report (CSV)
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-in-from-top-4 {
          from {
            opacity: 0;
            transform: translateY(-16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-in.slide-in-from-top-4 {
          animation: slide-in-from-top-4 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default QCAnalysisReport;
